'use client';

import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { OTPInput, OTPInputContext } from 'input-otp';
import { Minus } from 'lucide-react';
import * as React from 'react';

const inputOTPSlotVariants = cva(
  'relative flex min-w-0 flex-1 items-center justify-center border-input border-y border-r bg-field outline-none transition-all first:rounded-l-lg first:border-l last:rounded-r-lg group-has-aria-invalid/input-otp:border-destructive data-[active=true]:z-10 data-[active=true]:border-ring data-[active=true]:ring-2 data-[active=true]:ring-ring/30 group-has-aria-invalid/input-otp:data-[active=true]:ring-destructive/20',
  {
    variants: {
      size: {
        default: 'h-9 text-sm',
        lg: 'h-14 text-xl',
      },
    },
    defaultVariants: { size: 'default' },
  },
);

const inputOTPCaretVariants = cva('w-px animate-caret-blink bg-foreground', {
  variants: {
    size: {
      default: 'h-4',
      lg: 'h-6',
    },
  },
  defaultVariants: { size: 'default' },
});

type InputOTPSize = NonNullable<VariantProps<typeof inputOTPSlotVariants>['size']>;

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

type InputOTPProps = DistributiveOmit<React.ComponentProps<typeof OTPInput>, 'size'> & {
  containerClassName?: string;
  size?: InputOTPSize;
};

const InputOTPSizeContext = React.createContext<InputOTPSize>('default');

const InputOTP = ({ className, containerClassName, size = 'default', ...props }: InputOTPProps) => {
  return (
    <InputOTPSizeContext value={size}>
      <OTPInput
        data-slot="input-otp"
        containerClassName={cn(
          'group/input-otp flex w-full items-center gap-2 has-disabled:opacity-50',
          containerClassName,
        )}
        spellCheck={false}
        className={cn('disabled:cursor-not-allowed', className)}
        {...props}
      />
    </InputOTPSizeContext>
  );
};

const InputOTPGroup = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return <div data-slot="input-otp-group" className={cn('flex min-w-0 flex-1 items-center', className)} {...props} />;
};

type InputOTPSlotProps = React.ComponentProps<'div'> & {
  index: number;
};

const InputOTPSlot = ({ index, className, ...props }: InputOTPSlotProps) => {
  const inputOTPContext = React.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};
  const size = React.useContext(InputOTPSizeContext);

  return (
    <div
      data-slot="input-otp-slot"
      data-active={isActive}
      className={cn(inputOTPSlotVariants({ size }), className)}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className={inputOTPCaretVariants({ size })} />
        </div>
      )}
    </div>
  );
};

const InputOTPSeparator = (props: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="input-otp-separator"
      className="flex items-center text-muted-foreground [&_svg:not([class*='size-'])]:size-4"
      role="separator"
      {...props}
    >
      <Minus />
    </div>
  );
};

export { REGEXP_ONLY_DIGITS } from 'input-otp';
export type { InputOTPProps, InputOTPSize, InputOTPSlotProps };
export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot, inputOTPSlotVariants };
